import React from "react";
import BaseCallout, { type SharedCalloutProps } from "./BaseCallout";

export type CalloutProps = Omit<
  SharedCalloutProps,
  "centerContent" | "buttonClassName"
>;

/**
 * Callout Component
 *
 * A promotional callout component for the homepage hero section.
 * Based on Figma Design: Homepage v2.0 - Q1 2026 (node 3135-8361)
 *
 * Features:
 * - Fixed height (152px) for consistent hero section layout
 * - Theme support: sifted, pro, fintech, startuplife, climate, upround, deeptech, neutral, summit
 * - Left border accent color with semi-transparent background (except summit: yellow border + black-gray gradient)
 * - Optional text button with chevron icon
 * - Compact title (accent-16) and description (regular-16)
 *
 * Special themes:
 * - Summit: Dark gradient background (black to summit-charcoal), summit-yellow border, white text, sifted button
 *
 * This is a presentational component - parent controls all interactions.
 *
 * @component
 * @example
 * <Callout
 *   callout={{
 *     title: "Short, snappy header",
 *     description: "This section can be used for ad-hoc content",
 *     buttonLabel: "Learn more",
 *     accent: "sifted"
 *   }}
 *   href="/path"
 * />
 */
const Callout: React.FC<CalloutProps> = ({
  fixedHeight = true,
  titleSize = "small",
  ...props
}) => {
  return (
    <BaseCallout
      {...props}
      fixedHeight={fixedHeight}
      titleSize={titleSize}
      centerContent
    />
  );
};

Callout.displayName = "Callout";

export default Callout;
